import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { Modal } from '../components/Modal';
import { PasswordRequirements } from '../components/PasswordRequirements';
import { resetPassword } from '../services/api';
import { getPasswordResetErrorMessage } from '../utils/errorMessages';
import { ROUTES } from '../constants';
import { ResetPasswordFormData } from '../types';
import { validatePassword, getPasswordRequirements } from '../utils/validation';

export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState<ResetPasswordFormData>({
    email: location.state?.email || '',
    otp: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!formData.email) {
      navigate(ROUTES.FORGOT_PASSWORD);
    }
  }, [formData.email, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setError('');
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.otp || formData.otp.length !== 6) {
      newErrors.otp = 'Please enter a valid 6-digit OTP';
    }

    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      newErrors.newPassword = 'Password does not meet requirements';
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    const response = await resetPassword(formData);

    if (response.success) {
      setShowSuccessModal(true);
    } else {
      setError(getPasswordResetErrorMessage(response.error));
    }

    setLoading(false);
  };

  const handleModalClose = () => {
    navigate(ROUTES.LOGIN);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-3 sm:p-4">
      <Card className="w-full max-w-md">
        <button
          onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4 sm:mb-6 p-1 -ml-1 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span className="text-sm sm:text-base">Back</span>
        </button>

        <div className="text-center mb-6 sm:mb-8">
          <Logo size="md" className="justify-center mb-3 sm:mb-4" />
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Lock className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Reset Your Password
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mb-2">
            Enter the code we sent to
          </p>
          <p className="text-sm sm:text-base font-medium text-gray-900">
            {formData.email}
          </p>
        </div>

        {error && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs sm:text-sm text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            label="Verification Code"
            type="text"
            name="otp"
            value={formData.otp}
            onChange={handleChange}
            error={errors.otp}
            placeholder="Enter 6-digit code"
            maxLength={6}
            required
            autoFocus
          />

          <div>
            <div className="relative">
              <Input
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                error={errors.newPassword}
                placeholder="Enter new password"
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-8 sm:top-9 text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <PasswordRequirements requirements={getPasswordRequirements(formData.newPassword)} />
          </div>

          <div className="relative">
            <Input
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              placeholder="Re-enter new password"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-8 sm:top-9 text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            isLoading={loading}
            disabled={
              !formData.otp ||
              !formData.newPassword ||
              !formData.confirmPassword
            }
          >
            Reset Password
          </Button>
        </form>
      </Card>

      <Modal
        isOpen={showSuccessModal}
        onClose={handleModalClose}
        title="Password Reset Successful!"
        message="Your password has been reset successfully. You can now login with your new password."
        variant="success"
        actionLabel="Go to Login"
        onAction={handleModalClose}
        showCloseButton={false}
      />
    </div>
  );
};
